import { Alert, Button } from "reactstrap";
import { useState } from "react";

import PageEditor from "../Sidebar/SidebarAdd/PageEditor";
import useAddPage from "../../api/api-hooks/pages/useAddPage";
import AppDrawer from "../../components/Drawers/AppDrawer";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";
import AppSpinner from "../../elements/AppSpinner";
import { Page } from "../../types/GlobalInterfaces";

/**
 * Drawer used for creating a new page
 *
 * @param {boolean} - open - whether the drawer is open or not
 * @param {function} - handleClose - function to close the drawer
 * @param {string=} - folderID - id of the folder to add the page to. Defaults to null.
 */

interface AddPageDrawerI {
  open: boolean;
  handleClose: () => void;
  folderID?: string;
}
const AddPageDrawer = ({ open, handleClose, folderID }: AddPageDrawerI) => {
  const { workspace } = useWorkspace();
  const { addPage, addSubmitting } = useAddPage();
  const [resetData, setResetData] = useState(false);
  const [page, setPage] = useState<Page | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleModalClose = () => {
    setResetData(true);

    setUploading(false);
    handleClose();
  };

  const handleChange = (newSidebarItem: any) => {
    setPage(newSidebarItem);
  };

  const createPage = async () => {
    if (!page?.Name || addSubmitting || uploading) return;

    if (!page) {
      setError("No page data.");
      return;
    }
    setUploading(true);
    setError("");

    // @ts-ignore
    addPage({
      payload: {
        Name: page.Name,
        Description: page.Description,
        Privacy: page.Privacy,
        Icon: "mdi mdi-file-table-box-outline",
        ...(page?.IconURL && { IconURL: page.IconURL }),
        FolderID: folderID,
      },
      onSuccess: handleModalClose,
      // @ts-ignore
      onError: (errMessage: string) => {
        setError(errMessage);
        setUploading(false);
      },
    });
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle="New Page"
      headerDescription={`Creating a page inside of ${workspace?.Name}`}
      headerOptions={
        <Button
          size="sm"
          color="primary"
          onClick={createPage}
          style={{ width: 80 }}
          disabled={!page?.Name || addSubmitting || uploading}
        >
          {addSubmitting || uploading ? (
            <AppSpinner size="sm" color="white" />
          ) : (
            "Create"
          )}
        </Button>
      }
      body={
        <>
          {error && (
            <Alert color="danger">Failed to create page! {error}</Alert>
          )}
          <PageEditor
            onChange={handleChange}
            resetData={resetData}
            setResetData={setResetData}
            existingPage={null}
            onSubmit={createPage}
          />
        </>
      }
    />
  );
};

export default AddPageDrawer;
